import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputSwitch } from "primereact/inputswitch";

import { WallboxEntry, WallboxEroundMappingInput } from "../../../../types/onboarding";

interface WallboxFormValues {
  manufacturer: string;
  model_string: string;
  integrated: boolean;
  eround: WallboxEroundMappingInput | null;
}

interface WallboxEntryDialogProps {
  visible: boolean;
  entry: WallboxEntry | null; // null = add mode
  onHide: () => void;
  onSubmit: (values: WallboxFormValues) => Promise<boolean>;
}

const WallboxEntryDialog: React.FC<WallboxEntryDialogProps> = ({
  visible,
  entry,
  onHide,
  onSubmit,
}) => {
  const [manufacturer, setManufacturer] = useState("");
  const [modelString, setModelString] = useState("");
  const [integrated, setIntegrated] = useState(false);
  const [eroundManufacturer, setEroundManufacturer] = useState("");
  const [eroundModel, setEroundModel] = useState("");
  const [chargingPoints, setChargingPoints] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setManufacturer(entry?.manufacturer ?? "");
    setModelString(entry?.model_string ?? "");
    setIntegrated(entry?.integrated ?? false);
    setEroundManufacturer(entry?.eround?.manufacturer ?? "");
    setEroundModel(entry?.eround?.model ?? "");
    setChargingPoints(entry?.eround ? String(entry.eround.charging_points) : "");
    setError(null);
  }, [visible, entry]);

  // SOPS-169: a model is shown to customers only once eRound's names for it are
  // known. The mapping is all three fields or none.
  const points = parseInt(chargingPoints, 10);
  const mappingFields = [eroundManufacturer.trim(), eroundModel.trim(), chargingPoints.trim()];
  const mapped = mappingFields.every(Boolean) && points >= 1;
  const partlyMapped = mappingFields.some(Boolean) && !mapped;
  // Models shown before mappings existed stay shown until the backfill maps or
  // hides them. The switch can always turn a model off, but only on once mapped.
  const shownBeforeMappings = !!entry?.integrated && !entry?.eround;
  const showLocked = !integrated && !mapped;

  const submit = async () => {
    if (!manufacturer.trim() || !modelString.trim()) {
      setError("Manufacturer and model are both required.");
      return;
    }
    if (partlyMapped) {
      setError("Fill in the eRound manufacturer, model and charging points, or leave all three empty.");
      return;
    }
    if (integrated && !mapped && !shownBeforeMappings) {
      setError(
        entry?.eround
          ? "Hide the model from customers before removing its eRound mapping."
          : "Add the eRound mapping to show this model to customers."
      );
      return;
    }
    setSubmitting(true);
    try {
      const ok = await onSubmit({
        manufacturer: manufacturer.trim(),
        model_string: modelString.trim(),
        integrated,
        eround: mapped
          ? {
              manufacturer: eroundManufacturer.trim(),
              model: eroundModel.trim(),
              charging_points: points,
            }
          : null,
      });
      if (ok) onHide();
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button label="Save" icon="pi pi-check" onClick={submit} loading={submitting} />
    </div>
  );

  return (
    <Dialog
      header={entry ? "Edit catalogue entry" : "Add catalogue entry"}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "32rem" }}
    >
      <div className="field">
        <label htmlFor="wb-manufacturer" className="input-label">
          Manufacturer *
        </label>
        <InputText
          id="wb-manufacturer"
          value={manufacturer}
          onChange={(e) => setManufacturer(e.target.value)}
          className="w-full"
        />
      </div>
      <div className="field">
        <label htmlFor="wb-model" className="input-label">
          Model *
        </label>
        <InputText
          id="wb-model"
          value={modelString}
          onChange={(e) => setModelString(e.target.value)}
          className="w-full"
        />
      </div>

      <h4 className="mt-4 mb-2">eRound mapping</h4>
      <p className="text-500 text-sm mt-0">
        eRound's exact catalogue names, for example "KEBA" and "KeConect P30 | Typ2 Socket".
      </p>
      <div className="field">
        <label htmlFor="wb-eround-manufacturer" className="input-label">
          eRound manufacturer
        </label>
        <InputText
          id="wb-eround-manufacturer"
          value={eroundManufacturer}
          onChange={(e) => setEroundManufacturer(e.target.value)}
          className="w-full"
        />
      </div>
      <div className="field">
        <label htmlFor="wb-eround-model" className="input-label">
          eRound model
        </label>
        <InputText
          id="wb-eround-model"
          value={eroundModel}
          onChange={(e) => setEroundModel(e.target.value)}
          className="w-full"
        />
      </div>
      <div className="field">
        <label htmlFor="wb-charging-points" className="input-label">
          Charging points
        </label>
        <InputText
          id="wb-charging-points"
          value={chargingPoints}
          keyfilter="pint"
          onChange={(e) => setChargingPoints(e.target.value)}
          style={{ width: "6rem" }}
        />
      </div>

      <div className="flex align-items-center gap-2">
        <InputSwitch
          inputId="wb-integrated"
          checked={integrated}
          disabled={showLocked}
          onChange={(e) => setIntegrated(!!e.value)}
        />
        <label htmlFor="wb-integrated">Shown to customers</label>
      </div>
      {showLocked && (
        <small className="text-500 block mt-1">
          Add the eRound mapping to show this model to customers.
        </small>
      )}
      {error && <small className="p-error block mt-2">{error}</small>}
    </Dialog>
  );
};

export default WallboxEntryDialog;
